import React, { useState } from "react";
import { siteConfig } from "../../config/siteConfig";

const DemoModal = ({ isOpen, onClose, onRegister }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const DEMO_QUESTIONS = siteConfig.demo.questions;

  if (!isOpen) return null;

  const handleAnswer = (idx) => {
    if (isAnswered) return;
    setSelectedAnswer(idx);
    setIsAnswered(true);
  };

  const handleNext = () => {
    if (currentIdx >= DEMO_QUESTIONS.length - 1) {
      setIsFinished(true);
    } else {
      setCurrentIdx((prev) => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    }
  };

  const question = DEMO_QUESTIONS[currentIdx];
  const progressPercent = ((currentIdx + 1) / DEMO_QUESTIONS.length) * 100;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white w-full max-w-3xl max-h-[90vh] rounded-[2rem] md:rounded-[3rem] shadow-2xl overflow-y-auto animate-in fade-in zoom-in duration-300">
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {!isFinished && (
          <div className="px-6 pt-8 md:px-10 md:pt-10 flex items-center justify-between">
            <span className="px-4 py-1 bg-violet-100 text-violet-700 text-[10px] md:text-xs font-black rounded-full uppercase">
              Demo en vivo
            </span>
            <div className="flex items-center gap-3">
              <span className="text-xs md:text-sm font-bold text-slate-400">
                Pregunta {currentIdx + 1}/{DEMO_QUESTIONS.length}
              </span>
              <div className="h-2 w-16 md:w-24 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-600 transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          </div>
        )}

        <div className="p-6 md:p-10">
          {!isFinished ? (
            <div className="animate-fade">
              <h3 className="text-xl md:text-3xl font-black text-slate-800 leading-tight mb-6 md:mb-10">
                {question.q}
              </h3>

              <div className="grid gap-3 md:gap-4">
                {question.o.map((option, idx) => {
                  const isCorrect = idx === question.c;
                  const isSelected = idx === selectedAnswer;
                  const letter = String.fromCharCode(65 + idx);

                  let buttonClass = "border-slate-100 hover:border-violet-300 hover:bg-violet-50/50";
                  let iconClass = "bg-slate-100 text-slate-600 border-2 border-slate-200";

                  if (isAnswered) {
                    if (isCorrect) {
                      buttonClass = "border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50";
                      iconClass = "bg-emerald-500 text-white border-2 border-emerald-600";
                    } else if (isSelected && !isCorrect) {
                      buttonClass = "border-rose-500 bg-gradient-to-r from-rose-50 to-red-50";
                      iconClass = "bg-rose-500 text-white border-2 border-rose-600";
                    } else {
                      buttonClass = "border-slate-100 bg-slate-50/50 opacity-60";
                    }
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(idx)}
                      disabled={isAnswered}
                      className={`w-full text-left p-4 md:p-6 rounded-xl md:rounded-2xl border-2 font-bold text-slate-700 transition-all flex items-center gap-3 md:gap-4 ${buttonClass}`}
                    >
                      <span className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg md:rounded-xl text-xs md:text-sm font-bold shrink-0 ${iconClass}`}>
                        {letter}
                      </span>
                      <span className="flex-grow text-sm md:text-base">{option}</span>
                    </button>
                  );
                })}
              </div>

              {isAnswered && (
                <div className="mt-6 md:mt-8 animate-in slide-in-from-top-4 duration-500">
                  <div className="p-4 md:p-6 rounded-xl md:rounded-2xl bg-slate-50 border border-slate-200 mb-6">
                    <p className="font-black text-slate-800 mb-1 text-sm md:text-base">Explicación:</p>
                    <p className="text-slate-600 text-xs md:text-sm">{question.exp}</p>
                  </div>
                  <button onClick={handleNext} className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl md:rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 mb-2">
                    {currentIdx === DEMO_QUESTIONS.length - 1 ? "Ver resultados" : "Siguiente pregunta"}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 animate-fade pb-10">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-violet-100 rounded-3xl flex items-center justify-center mx-auto mb-6 md:mb-8 animate-bounce">
                <span className="text-3xl md:text-4xl">🚀</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">¡Buen trabajo!</h3>
              <p className="text-base md:text-lg text-slate-600 mb-8 md:mb-10">
                Has completado la demo gratuita. <br />
                <span className="font-bold text-violet-600 italic">
                  Si deseas continuar inscríbete hoy mismo gratis
                </span>
              </p>
              <div className="flex flex-col gap-4">
                <button onClick={onRegister} className="w-full py-4 md:py-5 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-lg md:text-xl font-black rounded-xl md:rounded-2xl shadow-xl hover:scale-[1.02] transition-all">
                  Registrarme Gratis Ahora
                </button>
                <button onClick={onClose} className="text-slate-400 font-bold hover:text-slate-600 underline decoration-slate-200">
                  Cerrar y volver
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
